import { 
  getJokeById, 
  getRandomJoke, 
  getJokesByCategory,
  trackJokeInteraction 
} from '@/services/jokeService';

const state = {
  currentJoke: null,
  jokes: [],
  loading: false,
  error: null,
  category: ''
};

const getters = {
  currentJoke: (state) => state.currentJoke,
  jokes: (state) => state.jokes,
  loading: (state) => state.loading,
  error: (state) => state.error,
  currentCategory: (state) => state.category,
  jokesCount: (state) => state.jokes.length,
  
  jokesByLanguage: (state, getters, rootState) => {
    const selectedLanguages = rootState.preferences.languages;
    return state.jokes.filter(joke => selectedLanguages.includes(joke.language));
  },
  
  getJokePreview: (state) => (joke, maxLength = 100) => {
    if (!joke || !joke.text) return '';
    
    if (joke.text.length <= maxLength) return joke.text;
    
    const truncated = joke.text.substring(0, maxLength);
    const lastSpace = truncated.lastIndexOf(' ');
    
    if (lastSpace > maxLength * 0.8) {
      return truncated.substring(0, lastSpace) + '...';
    }
    
    return truncated + '...';
  }
};

const mutations = {
  SET_CURRENT_JOKE(state, joke) {
    state.currentJoke = joke;
  },
  
  SET_JOKES(state, jokes) {
    state.jokes = jokes;
  },
  
  SET_LOADING(state, loading) {
    state.loading = loading;
  },
  
  SET_ERROR(state, error) {
    state.error = error;
  },
  
  SET_CATEGORY(state, category) {
    state.category = category;
  },
  
  CLEAR_JOKES(state) {
    state.jokes = [];
    state.currentJoke = null;
  },

  /**
   * Update joke's updatedAt timestamp in local state
   * Prevents unnecessary refetch when tracking interactions
   */
  UPDATE_JOKE_TIMESTAMP(state, jokeId) {
    // Update in jokes array
    const jokeIndex = state.jokes.findIndex(j => j.id === jokeId);
    if (jokeIndex !== -1) {
      state.jokes[jokeIndex].updatedAt = new Date();
    }
    
    // Update current joke if viewing single joke
    if (state.currentJoke && state.currentJoke.id === jokeId) {
      state.currentJoke.updatedAt = new Date();
    }
  },

  /**
   * Increment like count in local state (optimistic update)
   */
  INCREMENT_LIKES(state, jokeId) {
    const jokeIndex = state.jokes.findIndex(j => j.id === jokeId);
    if (jokeIndex !== -1) {
      state.jokes[jokeIndex].likes = (state.jokes[jokeIndex].likes || 0) + 1;
    }
    
    if (state.currentJoke && state.currentJoke.id === jokeId) {
      state.currentJoke.likes = (state.currentJoke.likes || 0) + 1;
    }
  },

  /**
   * Increment share count in local state (optimistic update)
   */
  INCREMENT_SHARES(state, jokeId) {
    const jokeIndex = state.jokes.findIndex(j => j.id === jokeId);
    if (jokeIndex !== -1) {
      state.jokes[jokeIndex].shares = (state.jokes[jokeIndex].shares || 0) + 1;
    }
    
    if (state.currentJoke && state.currentJoke.id === jokeId) {
      state.currentJoke.shares = (state.currentJoke.shares || 0) + 1;
    }
  }
};

const actions = {
  async fetchRandomJoke({ commit, state, rootState }) {
    commit('SET_LOADING', true);
    commit('SET_ERROR', null);

    try {
      const languages = rootState.preferences.languages;

      // Fetch all languages in parallel
      const results = await Promise.all(
        languages.map(language => getRandomJoke({
          language,
          category: state.category || undefined
        }))
      );

      const allJokes = results.filter(Boolean);

      if (allJokes.length > 0) {
        const randomJoke = allJokes[Math.floor(Math.random() * allJokes.length)];
        commit('SET_CURRENT_JOKE', randomJoke);
      } else {
        commit('SET_CURRENT_JOKE', null);
      }
    } catch (error) {
      commit('SET_ERROR', error.message);
      console.error('Error fetching random joke:', error);
    } finally {
      commit('SET_LOADING', false);
    }
  },
  
  async fetchJokeById({ commit }, jokeId) {
    commit('SET_LOADING', true);
    commit('SET_ERROR', null);

    try {
      const joke = await getJokeById(jokeId);
      commit('SET_CURRENT_JOKE', joke || null);
    } catch (error) {
      commit('SET_CURRENT_JOKE', null);
      commit('SET_ERROR', error.message);
      console.error('Error fetching joke:', error);
    } finally {
      commit('SET_LOADING', false);
    }
  },
  
  async fetchJokesByCategory({ commit, rootState }, category) {
    commit('SET_LOADING', true);
    commit('SET_ERROR', null);
    commit('SET_CATEGORY', category);

    try {
      const languages = rootState.preferences.languages;

      // Fetch all languages in parallel
      const results = await Promise.all(
        languages.map(language => getJokesByCategory(category, language))
      );

      let allJokes = results.flat();

      // Sort by creation date (newest first)
      allJokes.sort((a, b) => {
        const aTime = a.createdAt?.toMillis?.() || 0;
        const bTime = b.createdAt?.toMillis?.() || 0;
        return bTime - aTime;
      });

      commit('SET_JOKES', allJokes);
    } catch (error) {
      commit('SET_ERROR', error.message);
      console.error('Error fetching jokes by category:', error);
    } finally {
      commit('SET_LOADING', false);
    }
  },
  
  setCategory({ commit }, category) {
    commit('SET_CATEGORY', category);
  },
  
  clearJokes({ commit }) {
    commit('CLEAR_JOKES');
  },

  /**
   * Track joke interaction (like, share, view)
   * Updates updatedAt timestamp in Firestore
   * @param {Object} payload - { jokeId, interactionType }
   */
  async trackInteraction({ commit }, { jokeId, interactionType }) {
    try {
      // Optimistic UI update
      if (interactionType === 'like') {
        commit('INCREMENT_LIKES', jokeId);
      } else if (interactionType === 'share') {
        commit('INCREMENT_SHARES', jokeId);
      }
      
      commit('UPDATE_JOKE_TIMESTAMP', jokeId);
      
      // Track in Firestore
      await trackJokeInteraction(jokeId, interactionType);
      
      console.log(`✅ Tracked ${interactionType} for joke ${jokeId}`);
      return true;
    } catch (error) {
      console.error('Error tracking interaction:', error);
      
      // Rollback optimistic update on error (optional)
      // You could implement a ROLLBACK mutation here if needed
      
      return false;
    }
  }
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
};